/**
 * Form Field Width Standards
 * 
 * Character-based width tokens for consistent form field sizing across the application.
 * Uses CSS 'ch' units which scale with font size for better accessibility.
 * 
 * @see docs/FORM_FIELD_WIDTH_STANDARDS.md for usage guidelines
 */

export const FIELD_WIDTHS = {
  // Text inputs - based on expected content length
  xs: '4ch',    // Year, PIN, small codes (e.g., 2024)
  sm: '8ch',    // Dates, short codes (e.g., 01/01/2024)
  md: '20ch',   // Names, references (e.g., "John Smith")
  lg: '30ch',   // Email, descriptions (e.g., "user@example.com")
  xl: '40ch',   // Long text (e.g., full addresses)
  full: '100%', // Text areas, comments
  
  // Dropdowns/Comboboxes (wider to accommodate options + icon)
  dropdown_xs: '12ch',  // "Yes/No", "GBP/USD", months
  dropdown_sm: '20ch',  // Standard dropdowns (statuses, types)
  dropdown_md: '30ch',  // Longer options (categories)
  dropdown_lg: '40ch',  // Very long options (account names)
  dropdown_full: '100%' // Rare, contextual only
} as const;

export type FieldWidth = keyof typeof FIELD_WIDTHS;

/**
 * Field width mapping for common financial form fields
 * Use these as guidelines when implementing forms
 */
export const COMMON_FIELD_MAPPINGS = {
  // Dates and Times
  date: 'sm' as FieldWidth,
  time: 'sm' as FieldWidth,
  dateTime: 'md' as FieldWidth,
  year: 'xs' as FieldWidth,
  month: 'dropdown_xs' as FieldWidth,
  
  // Financial
  amount: 'md' as FieldWidth,
  currency: 'dropdown_xs' as FieldWidth,
  accountNumber: 'md' as FieldWidth,
  reference: 'md' as FieldWidth,
  transactionId: 'md' as FieldWidth,
  
  // Personal Information
  firstName: 'md' as FieldWidth,
  lastName: 'md' as FieldWidth,
  fullName: 'lg' as FieldWidth,
  email: 'lg' as FieldWidth,
  phone: 'md' as FieldWidth,
  
  // Address
  addressLine: 'xl' as FieldWidth,
  city: 'md' as FieldWidth,
  postcode: 'sm' as FieldWidth,
  country: 'dropdown_md' as FieldWidth,
  
  // Business
  companyName: 'lg' as FieldWidth,
  companyCode: 'sm' as FieldWidth,
  department: 'dropdown_md' as FieldWidth,
  role: 'dropdown_sm' as FieldWidth,
  
  // Status and Categories
  status: 'dropdown_sm' as FieldWidth,
  category: 'dropdown_md' as FieldWidth,
  type: 'dropdown_sm' as FieldWidth,
  
  // Descriptions and Notes
  shortDescription: 'xl' as FieldWidth,
  description: 'full' as FieldWidth,
  notes: 'full' as FieldWidth,
  comments: 'full' as FieldWidth,
  
  // Codes and IDs
  code: 'sm' as FieldWidth,
  id: 'md' as FieldWidth,
  pin: 'xs' as FieldWidth,
} as const;

/**
 * Get the CSS max-width value for a given field width token
 */
export function getFieldWidth(width: FieldWidth): string {
  return FIELD_WIDTHS[width];
}

/**
 * Get the recommended width for a common field type
 */
export function getCommonFieldWidth(fieldType: keyof typeof COMMON_FIELD_MAPPINGS): string {
  return FIELD_WIDTHS[COMMON_FIELD_MAPPINGS[fieldType]];
}
